import { createContext, useCallback, useContext } from "react";

import { ConfettiEffect, useConfetti } from "@/components/ui/confetti-effect";
import {
    ALERT_SOCKET_EVENTS,
    ALERT_SOCKET_NAMESPACE,
    type AlertSocketEvents,
    type AlertType,
} from "@/utils/alert-socket";

import { useSocket } from "./hooks/useSocket";

type ConfettiContextValue = {
    isActive: boolean;
    triggerConfetti: () => void;
    /** Trigger an alert on another user's device (e.g. admin → confetti on target user). Uses same socket connection. */
    triggerAlertForUser: (targetUserId: string, alertType: AlertType) => void;
};

const ConfettiContext = createContext<ConfettiContextValue | null>(null);

export function ConfettiProvider({ children }: { children: React.ReactNode }) {
    const { isActive, triggerConfetti } = useConfetti();

    const { useSocketCallback, emit } = useSocket<AlertSocketEvents>({
        namespace: ALERT_SOCKET_NAMESPACE,
    });

    useSocketCallback(ALERT_SOCKET_EVENTS.TRIGGERED_RESPONSE, (alertType) => {
        if (alertType === "confetti") {
            triggerConfetti();
            return;
        }

        // Admin remote reset: wipe cached settings (e.g. a stuck HOME_DASHBOARD_LAYOUT) and reload.
        if (alertType === "clear_local_storage") {
            localStorage.clear();
            window.location.reload();
        }
    });

    const triggerAlertForUser = useCallback(
        (targetUserId: string, alertType: AlertType) => {
            emit(ALERT_SOCKET_EVENTS.TRIGGER_REQUEST, { targetUserId, alertType });
        },
        [emit]
    );

    return (
        <ConfettiContext.Provider value={{ isActive, triggerConfetti, triggerAlertForUser }}>
            {children}
            <ConfettiEffect isActive={isActive} />
        </ConfettiContext.Provider>
    );
}

export function useConfettiContext(): ConfettiContextValue {
    const ctx = useContext(ConfettiContext);
    if (!ctx) {
        throw new Error("useConfettiContext must be used within ConfettiProvider");
    }
    return ctx;
}
